export const communityEmojis = ["👍", "❤️", "😂", "🎉", "😮", "🙏"] as const;
export type CommunityTarget = "post" | "discussion" | "comment";
export type ReactionSummary = { emoji: string; count: number; selected: boolean };
export type Reactions = { items: ReactionSummary[]; canReact: boolean };

export function optimisticReactionToggle(current: Reactions, emoji: string): { next: Reactions; emoji: string | null } {
  const nextEmoji = current.items.find((item) => item.selected)?.emoji === emoji ? null : emoji;
  const items = current.items.map((item) => ({
    emoji: item.emoji,
    count: Math.max(0, item.count - (item.selected ? 1 : 0) + (item.emoji === nextEmoji ? 1 : 0)),
    selected: item.emoji === nextEmoji,
  })).filter((item) => item.count > 0);
  if (nextEmoji && !items.some((item) => item.emoji === nextEmoji)) items.push({ emoji: nextEmoji, count: 1, selected: true });
  return { next: { ...current, items }, emoji: nextEmoji };
}

export type CommunityComment = { uuid: string; parentUuid: string | null; body: string; authorName: string | null; createdAt: string; updatedAt: string; deleted: boolean; canEdit: boolean; canDelete: boolean; reactions: ReactionSummary[] };
export type Comments = { items: CommunityComment[]; nextOffset: number | null; total: number; canComment: boolean };
export type Discussion = { uuid: string; title: string; body: string; authorName: string | null; createdAt: string; updatedAt: string; canEdit: boolean; canDelete: boolean; commentCount: number; reactions: ReactionSummary[] };
export type Discussions = { items: Discussion[]; nextOffset: number | null; canCreate: boolean };

export function appendCommentPage(current: CommunityComment[], incoming: CommunityComment[], pendingUuids: ReadonlySet<string>): CommunityComment[] {
  const existing = new Map(current.map((comment) => [comment.uuid, comment]));
  const loaded = current.filter((comment) => !pendingUuids.has(comment.uuid));
  const loadedUuids = new Set(loaded.map((comment) => comment.uuid));
  const incomingUuids = new Set(incoming.map((comment) => comment.uuid));
  return [
    ...loaded,
    ...incoming.filter((comment) => !loadedUuids.has(comment.uuid)).map((comment) => existing.get(comment.uuid) ?? comment),
    ...current.filter((comment) => pendingUuids.has(comment.uuid) && !incomingUuids.has(comment.uuid)),
  ];
}

export class CommunityRequestError extends Error {
  constructor(public readonly status: number) { super(`Community request failed: ${status}`); }
}
export async function communityRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/community/${path}`, { ...init, credentials: "same-origin", cache: "no-store" });
  if (!response.ok) throw new CommunityRequestError(response.status);
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
}
export function communityJson(method: string, body: unknown): RequestInit {
  return { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) };
}
export function communityLoginHref(returnTo = "/discussions"): string {
  return `/auth/start?${new URLSearchParams({ scope: "directory", returnTo })}`;
}
export function communityErrorKey(error: unknown) {
  if (error instanceof CommunityRequestError) {
    if (error.status === 401) return "community.sessionExpired" as const;
    if (error.status === 403) return "community.forbidden" as const;
    if (error.status === 404) return "community.notFound" as const;
    if (error.status === 422) return "community.invalid" as const;
    if (error.status === 429) return "community.tooMany" as const;
  }
  return "community.error" as const;
}
